import { useNavigate, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Card } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { PageContainer } from "@/components/layout/PageContainer";
import { useStripe } from "@/components/subscription/StripeProvider";
import { PersonalInfoSection } from "@/components/subscription/form-sections/PersonalInfoSection";
import { AddressSection } from "@/components/subscription/form-sections/AddressSection";
import { PaymentSection } from "@/components/subscription/form-sections/PaymentSection";
import type { SubscriptionFormData } from "@/components/subscription/types";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  address: z.string().min(5, "Please enter your full address"),
  city: z.string().min(2, "City is required"),
  state: z.string().min(2, "State is required"),
  zipCode: z.string().min(5, "Please enter a valid ZIP code"),
  cardNumber: z.string().min(16, "Please enter a valid card number"),
  expiryDate: z.string().regex(/^(0[1-9]|1[0-2])\/([0-9]{2})$/, "Must be in MM/YY format"),
  cvv: z.string().min(3, "Please enter a valid CVV"),
});

export default function SubscriptionForm() {
  const [searchParams] = useSearchParams();
  const planName = searchParams.get("plan");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { createCheckoutSession } = useStripe();

  const form = useForm<SubscriptionFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      cardNumber: "",
      expiryDate: "",
      cvv: "",
    },
  });

  const onSubmit = async (values: SubscriptionFormData) => {
    const priceId = searchParams.get("priceId");
    if (!priceId) {
      toast({
        title: "Error",
        description: "No price ID found. Please select a plan first.",
        variant: "destructive",
      });
      navigate('/pricing');
      return;
    }
    
    try {
      const checkoutUrl = await createCheckoutSession(priceId);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <PageContainer>
      <div className="max-w-3xl mx-auto py-8 px-4">
        <Card className="p-6">
          <h1 className="text-3xl font-bold mb-6">Subscribe to {planName} Plan</h1>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <PersonalInfoSection form={form} />
              <AddressSection form={form} />
              <div className="border-t pt-6 mt-6">
                <h2 className="text-xl font-semibold mb-4">Payment Information</h2>
                <PaymentSection form={form} />
              </div>
              <Button type="submit" className="w-full">
                Complete Subscription
              </Button>
            </form>
          </Form>
        </Card>
      </div>
    </PageContainer>
  );
}